import React, { useEffect, useMemo, useState } from 'react';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { AddMorphModal } from './AddMorphModal.tsx';
import { Button } from 'react-bootstrap';
import { AddGeneModal } from './AddGeneModal.tsx';
import { faDna, faList, faQrcode } from '@fortawesome/free-solid-svg-icons';
import { QRCodeScannerModal } from './QRCodeScannerModal.tsx';
import { driver } from 'driver.js';
import { CardSelect } from '../../utilities/shared_components/CardSelect.tsx';
import { Morph } from '../../../types/Morph.ts';
import { Gene } from '../../../types/Gene.ts';
import { Reptile } from '../../../types/Reptile.ts';
import { useRepToolContext } from '../../../RepToolContext.tsx';

export const AddPickModal = ({ closeModal, genes, morphs, status, updateGroups, isSpecies = false, allReptiles, lockStatus }: {
  closeModal: () => void;
  genes: Gene[];
  morphs: Morph[];
  status: string;
  updateGroups: () => void;
  isSpecies?: boolean;
  allReptiles?: boolean;
  lockStatus?: boolean;
}) => {
  const [openAddMorph, setOpenAddMorph] = useState(isSpecies && allReptiles);
  const [openAddGenes, setOpenAddGenes] = useState(false);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [selectedReptile, setSelectedReptile] = useState<Reptile | null>(null);
  const [selectedReptileAccronym, setSelectedReptileAccronym] = useState<string | null>(!allReptiles ? localStorage.getItem('selectedReptile') || null : null);
  const { appendControls } = useRepToolContext();
  const driverObj = driver({
    steps: [
      { element: '#addGenes', popover: { title: 'Add Genes', description: 'Add a reptile to your collection by selecting genes from the list of available genes. This option is recommended if the reptiles you are adding have any hets or if you can\'t find the specific morph from the list.' } },
      { element: '#add-morph', popover: { title: 'Add Morph', description: 'Add a reptile to your collection by selecting a morph from the list of available morphs.' } },
      { element: '#addQR', popover: { title: 'Add By QR Code', description: 'Add a reptile to your collection by scanning a RepTool QR code.' } },
    ]
  });
  const filteredGenes = useMemo(() => {
    return genes.filter(gene => gene.reptile === selectedReptileAccronym || !selectedReptile);
  }, [genes, selectedReptile]);

  const filteredMorphs = useMemo(() => {
    return morphs.filter(morph => morph.reptile === selectedReptileAccronym || !selectedReptile);
  }, [morphs, selectedReptile]);

  const currentlyIsSpecies = useMemo(() => {
    const reptileLookup = (window as any).reptiles.find((r: Reptile) => r.accronym === selectedReptileAccronym);
    return reptileLookup?.controls?.isSpecies || isSpecies
  }, [isSpecies, selectedReptile]);

  useEffect(() => {
    if (currentlyIsSpecies && allReptiles) {
      // there is only one option so we can skip the selection
      setOpenAddMorph(true);
    }
  }, [isSpecies, allReptiles]);

  useEffect(() => {
    if (!openAddGenes && !openAddMorph && !openScanModal) {
      appendControls(null, 'Add Pick Modal');
    }
  }, [openAddGenes, openAddMorph, openScanModal]);

  return (
    <>
      {openScanModal && <QRCodeScannerModal
        closeModal={() => setOpenScanModal(false)}
        updateGroups={updateGroups}
      />}
      {openAddGenes && <AddGeneModal
        updateGroups={() => { updateGroups(); closeModal(); }}
        closeModal={() => setOpenAddGenes(false)}
        genes={filteredGenes}
        morphs={filteredMorphs}
        status={status}
        lockStatus={lockStatus}
        preselectedReptile={selectedReptileAccronym || undefined}
      />}
      {openAddMorph && <AddMorphModal
        updateGroups={() => { updateGroups(); closeModal(); }}
        closeModal={() => setOpenAddMorph(false)}
        status={status}
        morphs={filteredMorphs}
        lockStatus={lockStatus}
        isSpecies={currentlyIsSpecies}
        preselectedReptile={selectedReptileAccronym || undefined}
      />}
      <ReptoolModal id='add-pick-modal' title={"Add To Collection"} closeModal={closeModal} showTutorial={() => driverObj.drive()} body={(
        <>
          <div className="col-12 col-md-8 col-lg-6 mx-auto text-light">
            {selectedReptileAccronym && <>
              {allReptiles && <div className='text-center mb-3'>
                <h5 className='text-light'>Selected Animal: {selectedReptile?.reptile_name}</h5>
              </div>}
              {!currentlyIsSpecies && <CardSelect
                id='addGenes'
                icon={faDna}
                text={'Add by Genetics'}
                selected={false}
                long
                onSelect={() => setOpenAddGenes(true)}
                smallText='Recommended if you are adding an animal with hets or if you know the genetic traits of the reptile.'
              />}
              <CardSelect
                id='add-morph'
                icon={faList}
                long
                text={`Add by ${currentlyIsSpecies ? 'Species' : 'Morph'}`}
                selected={false}
                onSelect={() => setOpenAddMorph(true)}
                smallText={!isSpecies ? `Select a ${currentlyIsSpecies ? 'species' : 'morph'} from the list of available ${currentlyIsSpecies ? 'species' : 'morphs'}. This option is the simplest but may not fully cover your animal's genetics.` : ''}
              />
              {!allReptiles && <CardSelect
                id='addQR'
                icon={faQrcode}
                text={'Add by QR Code'}
                selected={false}
                long
                onSelect={() => setOpenScanModal(!openScanModal)}
                smallText='Use this option if you are adding a reptile with a RepTool QR code.'
              />}
            </>}
            {allReptiles && !selectedReptile && <div className='text-center'>
              <h5 className='text-light mt-4'>Select an animal species to add</h5>
              <Button onClick={() => (window as any).setUpReptileSelect('', () => (reptileAccronym: string, reptile: Reptile) => {
                setSelectedReptile(reptile);
                setSelectedReptileAccronym(reptileAccronym);
              }, selectedReptile)}>
                Choose Animal
              </Button>
            </div>}
          </div>
        </>
      )} />
    </>
  )

}